/**
 * Power-set math utilities.
 *
 * All functions are pure and DOM-free. No Math.random().
 */

/**
 * Return all 2^n subsets of `els` in bitmask order (rank 0 = ∅, rank 2^n-1 = full set).
 * Each subset preserves the original element order.
 */
export function powerSet<T>(els: readonly T[]): T[][] {
  const n = els.length;
  const total = 1 << n;
  const result: T[][] = [];
  for (let mask = 0; mask < total; mask++) {
    const subset: T[] = [];
    for (let i = 0; i < n; i++) {
      if ((mask >> i) & 1) {
        subset.push(els[i] as T);
      }
    }
    result.push(subset);
  }
  return result;
}

/**
 * Return the bitmask rank of `subset` within `universe`.
 * Bit i is set iff `universe[i]` is a member of `subset`.
 * This is a bijection onto `0..2^n-1` when `subset` is a subset of `universe`.
 */
export function subsetRank<T>(subset: readonly T[], universe: readonly T[]): number {
  const members = new Set(subset as T[]);
  let rank = 0;
  for (let i = 0; i < universe.length; i++) {
    if (members.has(universe[i] as T)) {
      rank |= 1 << i;
    }
  }
  return rank;
}

/**
 * Return all covering pairs (Hasse diagram edges) for a power-set lattice of size n.
 * Two ranks are connected iff they differ by exactly one set bit (one element addition).
 * Each pair is `[lower, upper]` where `lower` has one fewer element than `upper`.
 */
export function latticeEdges(n: number): [number, number][] {
  const total = 1 << n;
  const edges: [number, number][] = [];
  for (let lo = 0; lo < total; lo++) {
    for (let hi = lo + 1; hi < total; hi++) {
      const diff = hi ^ lo;
      // Covering pair: hi = lo | singleBit, i.e. diff is a power of two and lo & diff === 0
      if ((diff & (diff - 1)) === 0 && (lo & diff) === 0) {
        edges.push([lo, hi]);
      }
    }
  }
  return edges;
}

/**
 * Map each subset rank to its bit-vector representation.
 * `coords[i]` is 1 if bit i is set in the rank, 0 otherwise.
 */
export function hypercubeCoords(n: number): { rank: number; coords: number[] }[] {
  const total = 1 << n;
  const result: { rank: number; coords: number[] }[] = [];
  for (let rank = 0; rank < total; rank++) {
    const coords: number[] = [];
    for (let i = 0; i < n; i++) {
      coords.push((rank >> i) & 1);
    }
    result.push({ rank, coords });
  }
  return result;
}
